import { useNavigate, useRouterState } from "@tanstack/react-router";
import { PUBLIC_LOCALES, US_MARKET_ENABLED } from "@/lib/markets";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { DEFAULT_LOCALE, LOCALES, useLocale, useMarket, useT, type Locale } from "@/i18n";

/**
 * Switcher options are region + language combinations. We add a virtual
 * "en-US" row that flips the market to US (USD, US shipping) on top of the
 * FI-market language rows.
 */
type SwitcherId = Locale | "en-US";

const ALL_OPTIONS: Array<{ id: SwitcherId; flag: string; label: string; sublabel?: string }> = [
  { id: "fi", flag: "🇫🇮", label: "Suomi" },
  { id: "en", flag: "🇬🇧", label: "English", sublabel: "EU · EUR" },
  { id: "sv", flag: "🇸🇪", label: "Svenska" },
  { id: "et", flag: "🇪🇪", label: "Eesti" },
  { id: "en-US", flag: "🇺🇸", label: "English", sublabel: "US · USD" },
];

/**
 * The US market is currently not operated, and locales whose content is not
 * fully translated are hidden from the public selector — see src/lib/markets.ts.
 */
const OPTIONS = ALL_OPTIONS.filter((o) =>
  o.id === "en-US" ? US_MARKET_ENABLED : PUBLIC_LOCALES[o.id],
);

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const currentLocale = useLocale();
  const market = useMarket();
  const currentId: SwitcherId = market === "us" ? "en-US" : currentLocale;
  const t = useT();
  const navigate = useNavigate();
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });

  const select = (target: SwitcherId) => {
    const next: Record<string, unknown> = { ...search };
    if (target === "en-US") {
      // Take the shopper to the dedicated, fluent English (US) landing
      // page rather than just flipping the market on the current URL.
      delete next.lang;
      delete next.market;
      navigate({ to: "/us", search: next as never, replace: false });
      return;
    } else {
      delete next.market;
      if (target === DEFAULT_LOCALE) delete next.lang;
      else next.lang = target;
    }
    navigate({ search: next as never, replace: false });
  };

  const currentFlag = OPTIONS.find((o) => o.id === currentId)?.flag ?? "🇫🇮";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        aria-label={`${t("lang.label")}: ${currentId === "en-US" ? "en·us" : currentId}`}
      >
        <Globe className="h-4 w-4" aria-hidden />
        <span aria-hidden>{currentFlag}</span>
        {!compact && (
          <span className="uppercase tracking-wide text-xs">
            {currentId === "en-US" ? "en·us" : currentId}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          {t("lang.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onSelect={() => select(o.id)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span aria-hidden className="text-base leading-none">{o.flag}</span>
            <span className="flex-1">
              {o.label}
              {o.sublabel && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {o.sublabel}
                </span>
              )}
            </span>
            {o.id === currentId && <Check className="h-3.5 w-3.5 text-foreground" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
// keep import of LOCALES to signal intent; underscore-suppress unused warn
void LOCALES;